/**
 * Approximate page counter. Not Fade In's true pagination (no MORE/CONT'D
 * logic) — an estimate for the status bar and info@pagecount. Counts wrapped
 * lines per paragraph (monospace metrics) plus each style's spacebefore, then
 * divides by the page's line budget.
 */
import type { Para } from "../format/types";
import type { PageMetrics } from "./metrics";

export const LINES_PER_PAGE_FALLBACK = 55;

/** Courier advance width ≈ 0.6em. */
const CHAR_WIDTH_EM = 0.6;

function linesPerPage(m: PageMetrics): number {
  const usable = m.pageHeightPx - m.marginTopPx - m.marginBottomPx;
  const n = Math.round(usable / m.lineHeightPx);
  return n > 0 ? n : LINES_PER_PAGE_FALLBACK;
}

export function estimatePageCount(paras: Para[], m: PageMetrics): number {
  if (paras.length === 0) return 1;

  const emPx = (m.fontSizePt * 96) / 72; // 12pt → 16px
  const charWidthPx = emPx * CHAR_WIDTH_EM;

  let totalLines = 0;
  for (const para of paras) {
    const sm = m.styles.get(para.style);
    const leftIndent = sm?.marginLeftPx ?? 0;
    const rightIndent = sm?.marginRightPx ?? 0;
    const width = Math.max(charWidthPx, m.contentWidthPx - leftIndent - rightIndent);
    const charsPerLine = Math.max(1, Math.floor(width / charWidthPx));

    const text = para.runs.map((r) => r.text).join("");
    const textLines = text.length === 0 ? 1 : Math.ceil(text.length / charsPerLine);

    const spaceBeforeLines =
      m.lineHeightPx > 0 ? (sm?.spaceBeforePx ?? 0) / m.lineHeightPx : 0;

    totalLines += textLines + spaceBeforeLines;
  }

  return Math.max(1, Math.ceil(totalLines / linesPerPage(m)));
}
